import styled from '@emotion/styled';
import { BaseButton } from '../base/button/button';
import { Tooltip } from '../base/tooltip/tooltip';
import { Variables } from '../base/variables/variables';
import { WEEK_COLUMNS } from '../../services/worker-report/worker-report.model';

const V = Variables.ThemeLight;

/* ---- Page header ---------------------------------------------------------------------------- */

export const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

export const Title = styled.h1`
    margin: 0;
    color: ${V.TextStrong};
    font-size: ${Variables.FontSizes.medium};
    font-weight: ${Variables.FontWeight.semibold};
`;

export const Snapshot = styled.div`
    color: ${V.TextMild};
    font-size: 12px;
`;

/* ---- Summary cards -------------------------------------------------------------------------- */

export const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
`;

/** A card is a filter: the whole tile is the button. */
export const CardButton = styled(BaseButton)<{ selected: boolean }>`
    display: block;
    width: 100%;
    padding: 0;
    text-align: left;
    border-radius: ${Variables.Rounded.medium};
    outline: ${({ selected }) => (selected ? `2px solid ${V.PrimaryConstant6}` : 'none')};
    outline-offset: -1px;

    & > div {
        height: 100%;
    }
`;

export const CardLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

export const Dot = styled.span<{ color: string }>`
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

/* ---- Worker Activity card ------------------------------------------------------------------- */

export const ActivityHead = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 16px 12px;
    border-bottom: 1px solid ${V.Border1};
`;

export const ActivityTitle = styled.div`
    color: ${V.TextStrong};
    font-size: 15px;
    font-weight: ${Variables.FontWeight.semibold};
`;

export const ActivitySub = styled.div`
    color: ${V.TextMild};
    font-size: 12px;
    margin-top: 4px;
    max-width: 420px;
`;

export const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const FilterLabel = styled.span`
    color: ${V.TextMild};
    font-size: 12px;
`;

export const StepSelect = styled.div`
    min-width: 200px;
`;

export const Swatch = styled.span<{ color: string }>`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 6px;
    background: ${({ color }) => color};
`;

/* ---- Heatmap -------------------------------------------------------------------------------- */

const COLUMNS = `minmax(240px, 2.4fr) repeat(${WEEK_COLUMNS}, minmax(56px, 1fr)) minmax(64px, 0.8fr)`;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: ${COLUMNS};
    column-gap: 3px;
    row-gap: 3px;
    padding: 12px 16px;
    overflow-x: auto;
`;

export const HeaderCell = styled.div<{ align?: 'left' | 'center' | 'right' }>`
    display: flex;
    align-items: center;
    justify-content: ${({ align }) => (align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center')};
    gap: 8px;
    padding: 4px 4px 8px;
    color: ${V.TextMild};
    font-size: 11px;
    font-weight: ${Variables.FontWeight.semibold};
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

export const ExpandAll = styled(BaseButton)`
    padding: 0;
    min-width: 0;
    font-size: 11px;
    text-transform: none;
    letter-spacing: 0;
`;

export const RowHeader = styled(BaseButton)<{ depth: 0 | 1; expandable: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    width: 100%;
    min-width: 0;
    height: 28px;
    padding: 0 4px 0 ${({ depth }) => (depth ? '34px' : '4px')};
    border-radius: ${Variables.Rounded.small};
    color: ${({ depth }) => (depth ? V.TextMedium : V.TextStrong)};
    font-size: 13px;
    font-weight: ${({ depth }) => (depth ? Variables.FontWeight.regular : Variables.FontWeight.semibold)};
    text-align: left;
    cursor: ${({ expandable }) => (expandable ? 'pointer' : 'default')};

    &:hover {
        background: ${({ expandable }) => (expandable ? V.Background1 : 'transparent')};
    }
`;

export const RowLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ChevronSlot = styled.span`
    display: inline-flex;
    flex: none;
    width: 16px;
    color: ${V.TextMild};
`;

export const ErrorBadge = styled.span`
    flex: none;
    padding: 1px 6px;
    border-radius: 10px;
    background: ${V.WarningMild3};
    color: ${V.WarningInverted4};
    font-size: 10px;
    font-weight: ${Variables.FontWeight.semibold};
`;

export const Cell = styled(BaseButton)<{ fill: string; wedge?: string; invert: boolean; selected: boolean }>`
    position: relative;
    width: 100%;
    min-width: 0;
    height: 28px;
    padding: 0;
    border-radius: ${Variables.Rounded.small};
    background: ${({ fill }) => fill};
    color: ${({ invert }) => (invert ? Variables.Swatches.White : V.TextStrong)};
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    font-variant-numeric: tabular-nums;
    overflow: hidden;
    box-shadow: ${({ selected }) => (selected ? `inset 0 0 0 2px ${V.TextStrong}` : 'none')};

    &::after {
        content: '';
        display: ${({ wedge }) => (wedge ? 'block' : 'none')};
        position: absolute;
        right: 0;
        bottom: 0;
        width: 12px;
        height: 12px;
        background: ${({ wedge }) => `linear-gradient(135deg, transparent 50%, ${wedge || 'transparent'} 50%)`};
    }

    &:hover {
        box-shadow: inset 0 0 0 2px ${V.BorderB1};
    }
`;

export const EmptyCell = styled.div`
    height: 28px;
    border-radius: ${Variables.Rounded.small};
    background: ${V.Background1};
`;

export const TotalCell = styled.div<{ depth: 0 | 1 }>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 4px;
    color: ${({ depth }) => (depth ? V.TextMild : V.TextMedium)};
    font-size: 12px;
    font-variant-numeric: tabular-nums;
`;

export const Message = styled.div`
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 16px;
    color: ${V.TextMild};
    font-size: 13px;
`;

export const Legend = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-top: 1px solid ${V.Border1};
    color: ${V.TextMild};
    font-size: 12px;
`;

export const LegendItems = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
`;

export const LegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${V.TextMedium};
`;

/* ---- Tooltip content ------------------------------------------------------------------------ */

export const Tip = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 220px;
    max-width: 320px;
    padding: 4px 2px;
    font-size: 12px;
    color: ${V.TextMedium};
`;

export const TipTitle = styled.div`
    color: ${V.TextStrong};
    font-weight: ${Variables.FontWeight.semibold};
`;

export const TipMuted = styled.div`
    color: ${V.TextMild};
`;

export const TipLine = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

/** The core Tooltip, filling its grid track: its own wrapper is inline and would shrink the cell to its text. */
export const GridTip = styled(Tooltip)`
    display: block;
    min-width: 0;
`;
